import React, { useState } from 'react'
import { Modal, Row, Col } from 'react-bootstrap'
import { Button } from '@abroad/components'

const CopyButton = ({ value, fieldId, copiedField, handleCopy }) => {
  const isCopied = copiedField === fieldId
  return (
    <span className='position-relative d-inline-block ms-1'>
      <button
        className={`copy-button custom-copy-btn mt-1 ${
          isCopied ? 'copied' : ''
        }`}
        onClick={() => handleCopy(value, fieldId)}
        aria-label={isCopied ? 'Copied' : 'Copy to clipboard'}>
        <div className='icon-container'>
          {/* Copy Icon */}
          <svg
            className='copy-icon'
            width='24'
            height='24'
            viewBox='0 0 64 64'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'>
            <rect
              x='20'
              y='8'
              width='32'
              height='44'
              rx='4'
              stroke='black'
              strokeWidth='3'
              fill='white'
            />
            <rect
              x='12'
              y='16'
              width='32'
              height='44'
              rx='4'
              stroke='black'
              strokeWidth='3'
              fill='white'
            />
            <line
              x1='24'
              y1='24'
              x2='36'
              y2='24'
              stroke='black'
              strokeWidth='3'
              strokeLinecap='round'
            />
            <line
              x1='24'
              y1='32'
              x2='36'
              y2='32'
              stroke='black'
              strokeWidth='3'
              strokeLinecap='round'
            />
            <line
              x1='24'
              y1='40'
              x2='36'
              y2='40'
              stroke='black'
              strokeWidth='3'
              strokeLinecap='round'
            />
          </svg>

          {/* Check Icon */}
          <svg
            className='check-icon'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'>
            <path
              d='M20 6L9 17L4 12'
              stroke='green'
              strokeWidth='3'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </button>
    </span>
  )
}

const renderField = (label, value, fieldId, copiedField, handleCopy) => (
  <div className='d-flex align-items-center justify-content-between'>
    <span className='s7 opacity-50'>{label}</span>
    <span className='d-flex align-items-center justify-content-between'>
      <span className={`s7 opacity-75`}>{value}</span>
      <CopyButton
        value={value}
        fieldId={fieldId}
        copiedField={copiedField}
        handleCopy={handleCopy}
      />
    </span>
  </div>
)

const renderBankDetails = (bank, type, copiedField, handleCopy) => (
  <div className='mb-3'>
    <div className='mb-2 s6'>
      {type === 'aba' ? 'ACH(US ONLY)/ABA Details' : 'SWIFT Details'}
    </div>
    <div
      className='bg-light p-3 rounded mb-2'
      style={{ fontFamily: 'monospace', fontSize: '1rem' }}>
      {renderField(
        'Account holder name',
        bank.account_holder_name,
        `${type}-account_holder_name`,
        copiedField,
        handleCopy,
      )}
      {bank.routing_number &&
        renderField(
          'Routing number',
          bank.routing_number,
          `${type}-routing_number`,
          copiedField,
          handleCopy,
        )}
      {bank.account_number &&
        renderField(
          'Account number',
          bank.account_number,
          `${type}-account_number`,
          copiedField,
          handleCopy,
        )}
      {type === 'swift' &&
        bank.swift_code &&
        renderField(
          'SWIFT code',
          bank.swift_code,
          `${type}-swift_code`,
          copiedField,
          handleCopy,
        )}
      {bank.reference &&
        renderField(
          'Reference',
          bank.reference,
          `${type}-reference`,
          copiedField,
          handleCopy,
        )}
      {renderField(
        'Bank name',
        bank.bank_name,
        `${type}-bank_name`,
        copiedField,
        handleCopy,
      )}
      {renderField(
        'Account type',
        bank.account_type.charAt(0).toUpperCase() + bank.account_type.slice(1),
        `${type}-account_type`,
        copiedField,
        handleCopy,
      )}
    </div>
  </div>
)

const renderAddress = (address) => (
  <div className='s7 opacity-50'>
    {address.line1 && <div>{address.line1}</div>}
    {address.line2 && <div>{address.line2}</div>}
    <div>
      {address.city}, {address.state} {address.postal_code},{' '}
    </div>
  </div>
)

const generateInstructionsText = (fundingInstructions) => {
  if (!fundingInstructions) return ''
  const { amount, bank_transfer } = fundingInstructions
  const financialAddresses = bank_transfer?.financial_addresses || []
  const aba = financialAddresses.find((addr) => addr.type === 'aba')?.aba
  // const swift = financialAddresses.find((addr) => addr.type === 'swift')?.swift

  let text = 'Payment for Invoice\n'
  text += `Amount: $${
    amount?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'
  }\n\n`

  if (aba) {
    text += 'ACH(US ONLY)/ABA Details\n'
    text += `Account holder name: ${aba.account_holder_name}\n`
    text += `Routing number: ${aba.routing_number}\n`
    text += `Account number: ${aba.account_number}\n`
    text += `Bank name: ${aba.bank_name}\n`
    text += `Account type: ${aba.account_type}\n\n`
  }
  // if (swift) {
  //   text += 'SWIFT Details\n'
  //   text += `Account holder name: ${swift.account_holder_name}\n`
  //   text += `Account number: ${swift.account_number}\n`
  //   text += `SWIFT code: ${swift.swift_code}\n`
  //   text += `Bank name: ${swift.bank_name}\n`
  //   text += `Account type: ${swift.account_type}\n\n`
  // }
  if (aba) {
    text += 'Addresses\n'
    text += `Bank address: ${aba.bank_address.line1}, ${aba.bank_address.city}, ${aba.bank_address.state} ${aba.bank_address.postal_code}\n`
  }
  return text
}

const downloadInstructions = (fundingInstructions) => {
  const text = generateInstructionsText(fundingInstructions)
  const blob = new Blob([text], { type: 'text/plain' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'bank-instructions.txt'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

const TransferInstructions = ({ fundingInstructions, onClose }) => {
  const show = Boolean(fundingInstructions)
  const [copiedField, setCopiedField] = useState(null)

  const handleCopy = (text, fieldId) => {
    navigator.clipboard.writeText(text)
    setCopiedField(fieldId)
    setTimeout(() => setCopiedField(null), 3000)
  }

  if (!show) return null

  const { amount, bank_transfer } = fundingInstructions
  const financialAddresses = bank_transfer?.financial_addresses || []
  const aba = financialAddresses.find((addr) => addr.type === 'aba')?.aba
  // const swift = financialAddresses.find((addr) => addr.type === 'swift')?.swift

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop='static'
      className='payment-modal quest-payment-modal'
      size='lg'>
      <Modal.Header className='payment-modal-header d-flex justify-content-center'>
        <h3 className='payment-modal-title mb-0'>Abroad Bank Information</h3>
      </Modal.Header>
      <Modal.Body className='payment-modal-body pb-0 mb-75px'>
        <div className='mb-2 s6'>Payment for Invoice</div>
        <div className='mb-2 s1'>
          $
          {amount?.toLocaleString(undefined, { minimumFractionDigits: 2 }) ||
            '0.00'}
        </div>
        <div className='mb-2 s7 opacity-50'>
          Transfer funds using the following bank information:
        </div>
        <Row>
          <Col sm={12} md={12}>
            {aba && renderBankDetails(aba, 'aba', copiedField, handleCopy)}
          </Col>
          {/* <Col sm={12} md={6}>
            {swift &&
              renderBankDetails(swift, 'swift', copiedField, handleCopy)}
          </Col> */}
        </Row>
        <div className='mb-1 s6'>Addresses</div>
        <div className='mb-2 s7 opacity-50'>
          Your bank may ask you for the address of the recipient bank as well as
          the address of the account holder.
        </div>
        <div className='mb-2 s7 '>
          <span className='opacity-50'>
            Once you complete your wire transfer, please let us know at{' '}
          </span>
          <a
            href='mailto:experiences@abroad.io'
            className='text-saffron-300 text-decoration-none'>
            experiences@abroad.io.
          </a>{' '}
        </div>
        {aba && (
          <>
            <div className='s6 mb-1'>Bank address</div>
            {renderAddress(aba.bank_address)}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className='border-0 d-flex justify-content-between justify-content-lg-end pt-0'>
        <Row className='d-flex justify-content-between'>
          <Col xl={4} lg={4} md={4} sm={4} xs={4}>
            <Button
              size='sm'
              onClick={onClose}
              className='text-uppercase line-height-normal s9 border-radius-normal mt-30px px-3 secondary-popup-button'>
              Close
            </Button>
          </Col>
          <Col xl={8} lg={8} md={8} sm={8} xs={8}>
            <Button
              variant='saffron'
              onClick={() => downloadInstructions(fundingInstructions)}
              className='text-uppercase border-radius-normal text-white hover:text-white mt-30px'
              size='sm'>
              Download
            </Button>
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  )
}

export default TransferInstructions
